<template>
  <div class="page_container myindex">
    <div class="seo-visually-hidden">
      <p>投影映射通过将普通物体转化为动态画布，彻底改变了视觉展示。借助Hecoos软件，专业人士可以将3D模型文件无缝集成到项目中，打造震撼的视觉体验。其先进的混合模式确保多台投影仪之间的平滑过渡，消除生硬边缘，呈现连贯图像。</p>
      <p>显示表面自定义功能允许将内容映射到复杂形状（如球体或弧形屏幕）。无论是弧幕还是LED显示表面，Hecoos都能精准控制每个像素。自动计算融合带功能可自动计算重叠区域，节省手动调整时间。</p>
      <p>大型装置中，集群渲染技术将工作负载分配到多台机器，保持高性能。摄像机追踪功能同步虚拟与现实元素，非常适合AR应用。通过实时灯具属性同步，灯光效果与投影内容完美对齐。</p>

      <p>完美的LED屏幕表现需要精准的定位像素和色彩管理。Hecoos软件提供包含主流LED品牌预配置文件的灯库，简化设置流程。解析亮度工具检测不均匀照明，而颜色校准确保面板间色彩一致。</p>
      <p>创意控制方面，色轮配置支持调色板微调，透明通道实现透明叠加。大型场馆中，屏幕组合位置调整轻松对齐多屏幕。网格数据功能辅助不规则布局（如砖墙设计）的对齐。</p>
      <p>Hecoos还支持DMX通道数据集成，同步灯光与屏幕内容。真实光柱效果模拟自然光线，增强虚拟舞台的真实感。通过LUT校色，色彩精度达到广播级标准。</p>

      <p>Hecoos的xR扩展技术无缝融合物理与虚拟世界。通过配置虚拟摄像机位，制作人可在CGI背景下实时拍摄演员表演。兼容FreeD协议的追踪设备系统确保毫米级精准对齐。</p>
      <p>对延迟敏感的应用，AR低延迟模式将延迟降至难以察觉的水平。软件集成Notch实现实时粒子效果，Notch显存清理优化GPU内存。xR前景球创造沉浸式环境，摄像机与采集卡关联简化多机位设置。</p>
      <p>遮罩模板和抠像等功能支持高级合成。渲染集群处理复杂场景，帧同步组保持设备间同步。通过NDI图像发送，内容可轻松跨网络共享。</p>

      <h2>优化你的 3D 模型文件工作流程：关键要点</h2>
      <p>在充满活力的 3D 设计与可视化领域，高效管理你的3D 模型文件至关重要。首先，要注意绝对位置和绝对偏移设置。这些参数确保 3D 场景中的物体精确定位，这对于实现无缝且精确的视觉呈现至关重要。例如，在创建复杂的建筑模型时，为墙壁和家具等每个组件设置正确的绝对位置，能使整个结构看起来更加逼真。</p>
      <p>另一个重要方面是图层的使用。就像画家使用不同图层来创作杰作一样，在 3D 设计中，图层可让你分别组织和管理模型组件。在设计过程中，你可以轻松隐藏或显示特定的图层，这简化了工作流程，让你更专注于模型的不同部分。</p>
      <p>动画也起着重要作用。为 3D 模型添加动画可以赋予它们生命力。你可以设置关键帧动画来控制物体随时间的移动、旋转和变形。例如，在产品设计展示中，关键帧动画可用于展示产品的功能或部件的运动方式。</p>
      <p>在共享和协作 3D 项目时，拥有合适的账号系统至关重要。有些平台可能要求账号密码必填，以确保工作的安全性和完整性。这样，只有授权用户才能访问和修改你宝贵的3D 模型文件。</p>
      <p>总之，掌握3D 模型文件、绝对位置、图层、动画、关键帧和账号管理等关键要素，你就能优化 3D 设计工作流程，创建出令人惊叹的高质量 3D 模型。</p>
    </div>
    <myHeader :isDark="false" />
    <!-- 设计即实现模块 -->
    <div class="text-wrapper" :class="{ 'animate-shrink': shouldShrink }">
      <div class="ss">
        <span v-for="(char, index) in text" :key="index" class="char" :class="{ 'fade-in-up': showText }">
          {{ char }}
        </span>
      </div>
    </div>
    <!-- 虚拟制作模块 -->
    <div class="page-container">
      <video autoplay loop muted class="bg-video">
        <source src="https://download.hecoos.com/enlightv/2025-27/a56f474dd8f4166105a7cf160a821f8b.mp4"
          type="video/mp4" />
      </video>
    </div>
    <!-- hecoos studio 模块 -->
    <div class="studio">
      <div class="hec wow fadeInUp">hecoos Studio</div>
      <div class="text1 wow fadeInUp">实现设计与现场执行的<br />无缝对接</div>
      <div class="text2"></div>
    </div>
    <div class="container">
      <div class="left-section">
        <ul class="sub-items">
          <li v-for="(item, index) in textList" :key="index" class="sub-item wow textAnimation" ref="textElement"
            @mouseenter="handleMouseEnter(index)" @mouseleave="handleMouseLeave(index)"
            :class="{ active: currentActive === index }" style="margin: 10px 0">
            {{ item.text }}
            <img v-show="item.isIconVisible"
              src="https://download.hecoos.com/enlightv/2025-17/baf2f9815851a54ff51cc3ebf97b8121.png" alt="蓝色图标"
              class="icon" />
          </li>
        </ul>
      </div>
      <div class="right-section">
        <div v-for="(content, index) in contents" :key="index" v-show="currentActive === index">
          <div class="test_up">
            <img :src="content.image" alt="场景图" class="scene-image wow fadeInUp" />
          </div>
          <p>{{ content.description }}</p>
          <div class="buttons">
            <button class="free-trial-btn" @click="handleClickStudioLink">
              开启免费体验
            </button>
            <button class="more-info-btn" @click="pageTransition('/studio')">
              了解更多
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- hecoos xR/VP模块 -->
    <div class="hecoosxR-VP">
      <p class="wow fadeInUp">hecoos xR / VP</p>
      <div class="text4 wow fadeInUp title1">于虚实之间绘制无界之景</div>
      <div class="flex-container">
        <div class="goKnow" @click="pageTransition('/virtualProduction')">
          <router-link class="router" to="">进一步了解</router-link>
        </div>
        <div class="flex-container_right">
          <img src="https://download.hecoos.com/enlightv/2025-17/a796ed2ff47b0f99e73186b6b50bec2d.gif" alt="" srcset=""
            class="wow fadeInUp" style="border-radius: 20px" />
        </div>
      </div>
    </div>
    <!-- hecoos Server模块 -->
    <div class="hecoosServer">
      <p class="wow fadeInUp">hecoos Server</p>
      <div class="gradient-text wow fadeInUp title1">现场播控与虚拟制作的呈现核心</div>
      <div class="media-server-wrapper">
        <video ref="hecoosVideo" src="https://download.hecoos.com/enlightv/2025-10/5041d9a4efe8822b28ec28af02b8213f.mp4"
          muted></video>
      </div>

      <h1 class="main-title wow fadeInUp">探索产品</h1>

      <div class="explore-product-wrap">
        <div class="product-block" v-for="(product, index) in products" :key="index">
          <h2 :style="{ color: product.color }">{{ product.name }}</h2>
          <p id="product-desc">{{ product.description }}</p>
          <!-- @click="pageTransition(product.url)" -->
          <!-- 进一步了解 -->
          <router-link class="learn-more-btn" :to="{ path: product.url, hash: '#tab' }">
            进一步了解
          </router-link>
        </div>
      </div>
    </div>



    <!-- 视频教程模块 -->
    <div class="videoContent">
      <p style="margin-bottom: 35px" class="wow fadeInUp">
        <strong>视频教程</strong>
      </p>
      <p style="font-size: 20px; font-weight: 300; letter-spacing: 0.5px" class="wow fadeInUp">
        探索专家创建的在线视频
      </p>
      <div class="image-container">
        <div class="target-image wow fadeInUp title1">
          <p>走进<span class="gradient-text title1">&nbsp;全新工作方法</span></p>
        </div>
        <router-link :to="{ name: 'videoTutorial', hash: '#specific-position' }" class="setting_a2">
          <div class="play-button wow fadeInUp">
            <span class="play-icon"></span>
          </div>
        </router-link>
      </div>
    </div>

    <!-- 案例展示模块 -->
    <div class="caseShow">
      <p style="margin-bottom: 35px" class="wow fadeInUp">
        <strong>案例展示</strong>
      </p>
      <p style="font-size: 20px; font-weight: 300; letter-spacing: 0.5px" class="wow fadeInUp">
        关于我们的最新消息
      </p>
      <div class="imgBox">
        <myBanner3 v-if="bannerList.length" :bannerList="bannerList"></myBanner3>
      </div>
      <router-link class="goLook" to="/casepresentation">查看全部案例</router-link>
    </div>
    <!-- 听听他们怎么说模块 -->
    <!-- <myText></myText> -->
    <myFooter />
    <BackToTop />
  </div>
</template>

<script>
import myHeader from "@/components/pc/myHeader";
import myFooter from "@/components/pc/myFooter";
import myText from "@/components/pc/myText";
import myBanner3 from "@/components/pc/myBanner3";
import BackToTop from "@/components/pc/BackToTop";
import { getCaseList, getCaseClassificationList } from "@/api/api";
import { mapState } from "vuex";
export default {
  name: "Index",

  data() {
    return {
      shouldShrink: false,
      text: "设计即实现",
      showText: false,
      isVisible: false,
      isScrollDisabled: false, // 新增：记录滚动是否被禁用
      isInView: false,
      currentActive: 0,
      textList: [
        { text: "三维场景预演", isIconVisible: false },
        { text: "投影光路设计", isIconVisible: false },
        { text: "灯光预演与预编程", isIconVisible: false },
        { text: "导播系统", isIconVisible: false },
        { text: "现场控制", isIconVisible: false },
      ],
      products: [
        {
          name: "Server-D",
          color: "cyan",
          description: "最大限度管控从展演的灵感到最终落地呈现的各个环节",
          url: "/mediaServer?section=0",
        },
        {
          name: "Server-C",
          color: "orange",
          description: "虚拟制作拍摄流程中的“核心枢纽”",
          url: "/mediaServer?section=1",
        },
        {
          name: "Server-R",
          color: "aquamarine",
          description: "超强渲染能力亦能为虚拟内容创作者提供贴心保障",
          url: "/mediaServer?section=2",
        },
      ],
      contents: [
        {
          image:
            "https://download.hecoos.com/enlightv/2025-17/db0271467ce619175dcb729f82b47aeb.png",
          description:
            "投影光路设计的hecoos以三维可视化界面预演完整的活动过程，精准模拟视频、灯光、机械、交互、导播等舞台元素，艺术家与工程师通过预演协作探讨并清晰确立演出方案的每一处细节，是提升现场执行效率与准确率的坚实保障。",
        },
        {
          image:
            "https://download.hecoos.com/enlightv/2025-17/c920cebf916124741a907c5ebe5314fa.png",
          description:
            "模拟真实投影机的光路和画面畸变，精准测量投影机在实际环境中的照度和遮挡区域，通过内置多个品牌多种参数的投影机，轻松完成各种复杂模型的光路设计。",
        },
        {
          image:
            "https://download.hecoos.com/enlightv/2025-17/cc8eb7b4e5e1a3ee6657e8d1ad5d92ee.png",
          description:
            "灯光效果对于舞台气氛的烘托极为重要，hecoos内置主流品牌灯具，基于Art-Net协议配接，实现灯光的预编程、现场真实灯具控制，模拟三维场景中真实光影效果，再现舞台现场灯光的绚丽多彩。",
        },
        {
          image:
            "https://download.hecoos.com/enlightv/2025-17/e64e64debd17a43b1e0c257cd34d2ea2.png",
          description:
            "内置多种类型摄像机(摇臂、轨道等)，提前预演摄像机拍摄画面，基于时间轴的控制方式，实现精准的导播切换。同时可基于CUE点管理生成电子台本，将导播设计和现场执行无缝对接，通过云端来实现协同工作，可完成无人值守的导播切换任务",
        },
        {
          image:
            "https://download.hecoos.com/enlightv/2025-17/50deaaeda2a03b2b6427f3fb7e8c6e48.png",
          description:
            "Studio将预演工程与现场执行环节轻松串联，作为项目调试阶段多媒体设备的核心控制枢纽，Studio 凭借其精准的通讯与控制能力，确保各设备之间稳定、协调地运行。多人协同调试使团队协作更加流畅高效。",
        },
      ],
      bannerList: [],
      observer: null,
    };
  },
  created() {
    this.getCaseLists();
  },
  computed: {
    ...mapState(["adminUserInfo"]),
  },
  mounted() {

    this.showText = true;
    // 延迟1秒后开始让盒子变小，可按需调整延迟时间
    setTimeout(() => {
      this.shouldShrink = true;
    }, 1100);
    window.addEventListener("scroll", this.handleScroll);
    const wow = new WOW({
      boxClass: "wow",
      animateClass: "animated",
      offset: 0,
      mobile: true,
      live: true,
    });
    wow.init();

    const video = this.$refs.hecoosVideo;
    const options = {
      root: null,
      rootMargin: "0px",
      // 当视频显示一半时触发回调
      threshold: 0.5,
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          video.currentTime = 0; // 重置视频播放进度
          video.play().catch((error) => {
            console.log("视频播放失败:", error);
          });
        } else {
          video.pause(); // 视频离开视口时暂停
        }
      });
    }, options);

    this.observer.observe(video);

    video.addEventListener("ended", () => {
      video.pause(); // 视频播放结束后定格
    });

    this.$nextTick(() => {
      const listItems = this.$refs.textElement;
      listItems.forEach((item, index) => {
        item.style.animationDelay = `${index * 0.1}s`;
      });
      window.addEventListener('popstate', this.handlePopState);
    });
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    const video = this.$refs.hecoosVideo;
    video.removeEventListener("ended", () => {
      video.pause();
    });
  },
  components: {
    myHeader,
    myFooter,
    myText,
    myBanner3,
    BackToTop,
  },
  methods: {
    handleClickStudioLink(){
      if(this.adminUserInfo && this.adminUserInfo.id){
        this.pageTransition('/personalCenter')
      }else{
        this.pageTransition('/register')
      }
    },
    pageTransition(url) {
      this.$router.push({
        path: url,
      });
    },

    handleMouseEnter(index) {
      this.textList[index].isIconVisible = true;
      this.currentActive = index;
    },
    handleMouseLeave(index) {
      // 这里可以选择在鼠标离开时恢复默认显示，或者保持当前状态
      // 若要恢复默认显示：
      // this.currentActive = 0;
      this.textList[index].isIconVisible = false;
    },
    getCaseLists() {
      getCaseClassificationList().then((res) => {
        const postData = {
          page: "1",
          limit: "8",
          type: res.data[1].id,
        };
        getCaseList(postData).then((res) => {
          this.bannerList = res.data;
          // 为每个 item 添加 showTitle 属性
          this.bannerList.forEach((item) => {
            this.$set(item, "showTitle", false);
          });
        });
      });
    },
  },
  watch: {},
};
</script>

<style scoped lang="scss">
@import "@/styles/banner.scss";

// 设计即实现模块css
.seo-visually-hidden{
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.setting_a {
  text-decoration: none;
  color: #fff;
}

.setting_a2 {
  text-decoration: none;
  color: #fff;
}

.setting_a1 {
  text-decoration: none;
  color: #fff;
}

.setting_a1:hover {
  text-decoration: none;
  color: #2a82e4;
}

.out_box_ifr {
  width: 100%;
  height: 100vh;
  position: relative;

  iframe {
    width: 100%;
    height: 100vh;
  }
}
</style>
